import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage, getLocale } from '../../contexts/LanguageContext';
import { useNotification } from '../../contexts/NotificationContext';
import PageHeader from '../ui/PageHeader';
import Icon from '../ui/Icon';
import CreateSaisonPage from './CreateSaisonPage';
import api from '../../api';
import './SaisonPage.css';

const STATUS_FILTERS = ['all', 'active', 'finished'];
const STATUS_PRIORITY = { active: 0, upcoming: 1, finished: 2, unknown: 3 };

const SORTS = {
    newest:   { field: 'startDate', order: 'desc' },
    oldest:   { field: 'startDate', order: 'asc' },
    end:      { field: 'endDate', order: 'desc' },
    title:    { field: 'title', order: 'asc' },
    modified: { field: 'modifiedBy', order: 'asc' },
};

const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString(getLocale(), { timeZone: 'Europe/Paris', day: '2-digit', month: '2-digit', year: 'numeric' });

const getSeasonStatus = (season) => {
    const now = new Date();
    const startDate = new Date(season.startDate);
    const endDate = new Date(season.endDate);

    if (now < startDate) return 'upcoming';
    if (now >= startDate && now <= endDate) return 'active';
    if (now > endDate) return 'finished';
    return 'unknown';
};

const sortValue = (season, field) => {
    if (field === 'startDate' || field === 'endDate') return new Date(season[field]).getTime();
    return (season[field] || '').toLowerCase();
};

const SaisonPage = () => {
    const [seasons, setSeasons] = useState([]);
    const [loading, setLoading] = useState(true);
    const { serverId } = useParams();
    const navigate = useNavigate();
    const { t } = useLanguage();
    const { success, error } = useNotification();
    // ?create=1 (lien « Créer une saison » du tableau de bord) ouvre directement la création
    const [searchParams, setSearchParams] = useSearchParams();
    const [showCreateModal, setShowCreateModal] = useState(searchParams.get('create') === '1');
    const [refreshKey, setRefreshKey] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [sortKey, setSortKey] = useState('newest');

    useEffect(() => {
        if (!serverId) return;
        setLoading(true);
        api.get(`/servers/${serverId}/saisons`)
            .then(response => {
                setSeasons(Array.isArray(response.data) ? response.data : []);
            })
            .catch(err => {
                console.error("❌ Erreur lors de la récupération des saisons :", err);
                setSeasons([]);
            })
            .finally(() => setLoading(false));
    }, [serverId, refreshKey]);

    const refreshSeasons = () => setRefreshKey(prev => prev + 1);

    const handleDeleteSeason = (saisonId, saisonTitle) => {
        if (window.confirm(`⚠️ ${t('seasons.confirmDelete', 'Voulez-vous vraiment supprimer cette saison')} "${saisonTitle}" ?\n\n${t('common.irreversible', 'Cette action est irréversible')} !`)) {
            api.delete(`/saison/${saisonId}`)
                .then(() => {
                    setSeasons(prev => prev.filter(s => s._id !== saisonId));
                    success(`✅ ${t('seasons.deleteSuccess', 'Saison supprimée avec succès')} !`);
                })
                .catch(err => {
                    console.error("❌ Erreur suppression saison :", err);
                    error(`❌ ${t('seasons.deleteError', 'Une erreur est survenue lors de la suppression')}.`);
                });
        }
    };

    const handleToggleStatsVisibility = (saisonId, currentValue) => {
        const newValue = currentValue === false;
        api.patch(`/saison/${saisonId}/stats-visibility`, { visibleInStats: newValue })
            .then(() => {
                setSeasons(prev => prev.map(s =>
                    s._id === saisonId ? { ...s, visibleInStats: newValue } : s
                ));
                success(newValue
                    ? t('seasonsPage.visibleInStats', '✅ Saison visible dans les stats membres')
                    : t('seasonsPage.hiddenFromStats', '🚫 Saison masquée des stats membres'));
            })
            .catch(err => {
                console.error('❌ Erreur toggle stats visibility:', err);
                error(t('seasonsPage.visibilityError', '❌ Impossible de modifier la visibilité'));
            });
    };

    const handleToggleArchive = (season) => {
        const archived = !season.archived;
        api.patch(`/servers/${serverId}/saisons/${season._id}/archive`, { archived })
            .then(res => {
                setSeasons(prev => prev.map(s => (s._id === season._id ? { ...s, ...res.data } : s)));
                success(archived ? t('seasonsPage.archived', 'Saison archivée') : t('seasonsPage.unarchived', 'Saison désarchivée'));
            })
            .catch(err => {
                console.error('❌ Erreur archivage saison :', err);
                error(t('seasonsPage.archiveError', "Impossible de modifier l'archivage"));
            });
    };

    // Recherche + filtre de statut + tri, appliqués aux saisons actives comme archivées
    const sort = SORTS[sortKey];
    const visibleSeasons = seasons
        .filter(season => {
            const term = searchTerm.toLowerCase();
            const matchesSearch = season.title.toLowerCase().includes(term)
                || (season.modifiedBy && season.modifiedBy.toLowerCase().includes(term));
            return matchesSearch && (statusFilter === 'all' || getSeasonStatus(season) === statusFilter);
        })
        .sort((a, b) => {
            // Les saisons en cours d'abord, puis à venir, puis terminées ; le tri choisi s'applique dans chaque groupe
            const priority = STATUS_PRIORITY[getSeasonStatus(a)] - STATUS_PRIORITY[getSeasonStatus(b)];
            if (priority !== 0) return priority;
            const aValue = sortValue(a, sort.field);
            const bValue = sortValue(b, sort.field);
            if (aValue < bValue) return sort.order === 'asc' ? -1 : 1;
            if (aValue > bValue) return sort.order === 'asc' ? 1 : -1;
            return 0;
        });

    const currentSeasons = visibleSeasons.filter(s => !s.archived);
    const archivedSeasons = visibleSeasons.filter(s => s.archived);
    const activeCount = seasons.filter(s => !s.archived && getSeasonStatus(s) === 'active').length;

    const statusLabels = {
        active: t('seasonsPage.filterActive', 'En cours'),
        upcoming: t('seasonsPage.filterUpcoming', 'À venir'),
        finished: t('seasonsPage.filterFinished', 'Terminées'),
        all: t('seasonsPage.filterAll', 'Toutes'),
    };

    const renderStatus = (season) => {
        const status = getSeasonStatus(season);
        if (status === 'active') return <span className="c2-badge c2-badge--green"><span className="c2-diamond" />{t('seasonsPage.filterActive', 'En cours')}</span>;
        if (status === 'upcoming') return <span className="c2-badge c2-badge--purple"><span className="c2-diamond c2-diamond--purple" />{t('seasons.upcoming', 'À venir')}</span>;
        return <span className="c2-badge"><span className="c2-diamond c2-diamond--outline" />{t('seasons.finished', 'Terminée')}</span>;
    };

    const renderTable = (list) => (
        <div className="c2-table-wrap">
            <table className="c2-table seasons-table">
                <thead>
                    <tr>
                        <th>{t('seasonsPage.colSeason', 'Saison')}</th>
                        <th>{t('seasonsPage.colStatus', 'Statut')}</th>
                        <th>{t('seasonsPage.colPeriod', 'Période')}</th>
                        <th>{t('seasonsPage.colModifiedBy', 'Modifié par')}</th>
                        <th className="is-center">{t('seasonsPage.colMemberStats', 'Stats membres')}</th>
                        <th><span className="visually-hidden">{t('common.actions', 'Actions')}</span></th>
                    </tr>
                </thead>
                <tbody>
                    {list.map(season => {
                        const visible = season.visibleInStats !== false;
                        return (
                            <tr key={season._id} className={`seasons-row seasons-row--${season.archived ? "archived" : getSeasonStatus(season)}`}>
                                <td>
                                    <div className="seasons-title">
                                        <Link to={`/server/${serverId}/saisons/${season._id}/edit`} className="seasons-title__link">
                                            {season.title}
                                        </Link>
                                        {season.autoPublish && (
                                            <span className="c2-chip c2-chip--purple" title={t('seasons.autoPublishEnabled', 'Publication automatique activée')}>
                                                {t('seasonsPage.autoPublish', 'Publication auto')}
                                            </span>
                                        )}
                                    </div>
                                </td>
                                <td>{renderStatus(season)}</td>
                                <td className="seasons-period">
                                    {formatDate(season.startDate)} <span className="c2-muted">→</span> {formatDate(season.endDate)}
                                </td>
                                <td className="c2-muted">{season.modifiedBy || '—'}</td>
                                <td className="is-center">
                                    <button
                                        type="button"
                                        role="switch"
                                        aria-checked={visible}
                                        className={`c2-switch${visible ? ' is-on' : ''}`}
                                        onClick={() => handleToggleStatsVisibility(season._id, season.visibleInStats)}
                                        title={visible ? t('seasonsPage.hideFromStats', 'Masquer des stats membres') : t('seasonsPage.showInStats', 'Afficher dans les stats membres')}
                                        aria-label={`${t('seasonsPage.colMemberStats', 'Stats membres')} : ${season.title}`}
                                    >
                                        <span className="c2-switch__knob" />
                                    </button>
                                </td>
                                <td className="is-num">
                                    <div className="seasons-actions">
                                        <button
                                            type="button"
                                            className="c2-icon-btn c2-icon-btn--sm"
                                            onClick={() => navigate(`/server/${serverId}/saisons/${season._id}/edit`)}
                                            title={t('seasons.edit', 'Modifier la saison')}
                                            aria-label={`${t('seasons.edit', 'Modifier la saison')} : ${season.title}`}
                                        >
                                            <Icon name="pencil" size={16} />
                                        </button>
                                        <button
                                            type="button"
                                            className="c2-icon-btn c2-icon-btn--sm"
                                            onClick={() => handleToggleArchive(season)}
                                            title={season.archived ? t('seasonsPage.unarchive', 'Désarchiver') : t('seasonsPage.archive', 'Archiver')}
                                            aria-label={`${season.archived ? t('seasonsPage.unarchive', 'Désarchiver') : t('seasonsPage.archive', 'Archiver')} : ${season.title}`}
                                        >
                                            <Icon name="archive" size={16} />
                                        </button>
                                        <button
                                            type="button"
                                            className="c2-icon-btn c2-icon-btn--sm c2-icon-btn--danger"
                                            onClick={() => handleDeleteSeason(season._id, season.title)}
                                            title={t('seasons.delete', 'Supprimer la saison')}
                                            aria-label={`${t('seasons.delete', 'Supprimer la saison')} : ${season.title}`}
                                        >
                                            <Icon name="trash" size={16} />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );

    const hasFilters = searchTerm || statusFilter !== 'all';

    return (
        <div className="c2-scope c2-admin-page">
            <PageHeader
                icon="trophy"
                title={t('seasonsPage.title', 'Saisons')}
                subtitle={t('seasonsPage.subtitle', 'Créez et gérez vos saisons de GDC')}
                actions={
                    <>
                        <button type="button" className="c2-icon-btn" onClick={refreshSeasons} title={t('common.refresh', 'Actualiser')} aria-label={t('common.refresh', 'Actualiser')}>
                            <Icon name="refresh" size={18} />
                        </button>
                        <button type="button" className="c2-btn c2-btn--ghost" onClick={() => navigate(`/server/${serverId}/saisons/merge`)}>
                            <Icon name="copy" size={18} />
                            {t('seasonsPage.merge', 'Fusion des classements')}
                        </button>
                        <button type="button" className="c2-btn c2-btn--primary" onClick={() => setShowCreateModal(true)}>
                            <Icon name="plus" size={18} />
                            {t('seasonsPage.create', 'Créer une saison')}
                        </button>
                    </>
                }
            />

            <div className="seasons-meta">
                <span className="c2-chip">{seasons.length} {seasons.length > 1 ? t('seasons.seasonsPlural', 'saisons') : t('seasons.seasonSingular', 'saison')}</span>
                {activeCount > 0 && <span className="c2-chip c2-chip--green">{activeCount} {t('seasonsPage.filterActive', 'En cours').toLowerCase()}</span>}
            </div>

            {/* Barre de filtres */}
            <div className="seasons-toolbar">
                <div className="seasons-filters" role="group" aria-label={t('seasonsPage.colStatus', 'Statut')}>
                    {STATUS_FILTERS.map(filter => (
                        <button
                            key={filter}
                            type="button"
                            className={`seasons-filter${statusFilter === filter ? ' is-active' : ''}`}
                            onClick={() => setStatusFilter(filter)}
                            aria-pressed={statusFilter === filter}
                        >
                            {statusLabels[filter]}
                        </button>
                    ))}
                </div>
                <div className="seasons-toolbar__right">
                    <input
                        type="search"
                        className="c2-input c2-input--sm seasons-search"
                        placeholder={`${t('common.search', 'Rechercher')}...`}
                        aria-label={t('common.search', 'Rechercher')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <select
                        className="c2-select seasons-sort"
                        value={sortKey}
                        onChange={(e) => setSortKey(e.target.value)}
                        aria-label={t('seasonsPage.sortLabel', 'Trier')}
                    >
                        <option value="newest">{t('seasonsPage.sortNewest', 'Plus récentes')}</option>
                        <option value="oldest">{t('seasonsPage.sortOldest', 'Plus anciennes')}</option>
                        <option value="end">{t('seasonsPage.sortEnd', 'Date de fin')}</option>
                        <option value="title">{t('seasonsPage.sortTitle', 'Titre')}</option>
                        <option value="modified">{t('seasonsPage.sortModified', 'Modifié par')}</option>
                    </select>
                </div>
            </div>

            {loading ? (
                <div className="c2-loading">
                    <div className="c2-spinner" />
                    <p>{t('seasons.loading', 'Chargement des saisons')}...</p>
                </div>
            ) : seasons.length === 0 ? (
                <section className="c2-card c2-empty">
                    <Icon name="trophy" size={36} />
                    <h2>{t('seasons.noSeasons', 'Aucune saison créée')}</h2>
                    <p>{t('seasons.noSeasonsMessage', 'Commencez par créer votre première saison pour organiser vos compétitions.')}</p>
                    <button type="button" className="c2-btn c2-btn--primary" onClick={() => setShowCreateModal(true)}>
                        <Icon name="plus" size={18} />
                        {t('seasons.createFirstSeason', 'Créer la première saison')}
                    </button>
                </section>
            ) : (
                <>
                    <section className="c2-card seasons-card">
                        {currentSeasons.length > 0 ? renderTable(currentSeasons) : (
                            <div className="c2-empty">
                                <p>{t('seasons.noResultsMessage', 'Aucune saison ne correspond à vos critères de recherche.')}</p>
                                {hasFilters && (
                                    <button type="button" className="c2-btn c2-btn--ghost c2-btn--sm" onClick={() => { setSearchTerm(''); setStatusFilter('all'); }}>
                                        {t('common.clearFilters', 'Effacer les filtres')}
                                    </button>
                                )}
                            </div>
                        )}
                    </section>

                    {archivedSeasons.length > 0 && (
                        <details className="c2-card seasons-archived">
                            <summary>
                                <Icon name="archive" size={18} />
                                {t('seasonsPage.archivedSection', 'Saisons archivées ({n})').replace('{n}', archivedSeasons.length)}
                                <Icon name="chevronDown" size={18} className="seasons-archived__chevron" />
                            </summary>
                            <p className="c2-field__hint seasons-archived__hint">
                                {t('seasonsPage.archivedHint', 'Les saisons archivées restent intactes : elles sont simplement rangées ici.')}
                            </p>
                            {renderTable(archivedSeasons)}
                        </details>
                    )}
                </>
            )}

            {showCreateModal && (
                <CreateSaisonPage
                    onClose={(created) => {
                        setShowCreateModal(false);
                        if (searchParams.has('create')) setSearchParams({}, { replace: true });
                        if (created) refreshSeasons();
                    }}
                />
            )}
        </div>
    );
};

export default SaisonPage;
